import type { TransportConfigSwitchNotice } from '@/components/building-block-form/utils/switchValidation'
import type { TransportConfig } from '@/stores/type/transportConfig.type'

export type SelectOption = {
  value: string
  title: string
}

export type NewTransportConfigModel = Record<string, string | number | boolean | undefined>

type SelectableItem = {
  id: number | string
  name: string
  calculation: number
}

export const ALL_TRANSPORT_CONFIG_VALUE = 'all'
export const ALL_TRANSPORT_CONFIG_TITLE = 'All'
export const DEFAULT_TRANSPORT_DISTANCE = 0
export const NEW_TRANSPORT_CONFIG_ID = 0

export const createEmptyTransportConfigModel = (): NewTransportConfigModel => ({
  building_block: undefined,
  stage: undefined,
  transport_category: undefined,
  code: undefined,
  distance: DEFAULT_TRANSPORT_DISTANCE,
  apply_to_all_building_block: false,
  apply_to_all_code: false
})

export const createSelectOptions = <T extends SelectableItem>(
  items: T[],
  calculationId: number
): SelectOption[] => {
  const filtered = items
    .filter((item) => item.calculation === calculationId)
    .map((item) => ({
      value: String(item.id),
      title: item.name
    }))

  return [{ value: ALL_TRANSPORT_CONFIG_VALUE, title: ALL_TRANSPORT_CONFIG_TITLE }, ...filtered]
}

const uniqueMissingMaterialNames = (
  rows: NonNullable<TransportConfigSwitchNotice['missingRows']>
): string[] => {
  const uniqueRows: Record<string, string> = {}
  for (const row of rows) {
    const key = `${row.material_id}-${row.block_id}`
    if (uniqueRows[key] === undefined) {
      uniqueRows[key] = row.product_name
    }
  }
  return Object.values(uniqueRows)
}

export const resolveTransportNoticeTitle = (
  notice: TransportConfigSwitchNotice | null | undefined
): string => {
  const base = notice?.baseDatabaseName
  const target = notice?.targetDatabaseName
  if (base && target) {
    return `Some transport configuration rows were removed after switching database from ${base} to ${target}.`
  }
  return 'Some transport configuration rows were removed after database switch.'
}

export const buildTransportMissingMessages = (
  notice: TransportConfigSwitchNotice | null | undefined
): string[] => {
  const rows = notice?.missingRows ?? []
  return uniqueMissingMaterialNames(rows).map(
    (productName) =>
      `Affected material ${productName} is missing in the selected database. The related transport configuration row was removed.`
  )
}

export const isTransportConfigComplete = (config: TransportConfig): boolean => {
  return Boolean(
    (config.building_block != null || config.apply_to_all_building_block === true) &&
      config.stage &&
      (config.code != null || config.apply_to_all_code === true) &&
      config.distance != null
  )
}

export const resolveTransportCategory = (
  transportCategory: string | number | boolean | undefined
): string | null => {
  if (
    transportCategory == null ||
    transportCategory === '' ||
    transportCategory === ALL_TRANSPORT_CONFIG_VALUE
  ) {
    return null
  }

  return String(transportCategory)
}
